/**
 * 스마트 가이드라인 유틸리티
 * - 오브젝트 정렬 포인트 계산, 스냅, 구간 제한 렌더링
 * - Fabric.js v7: getCenterPoint(), getScaledWidth/Height()는 캔버스 좌표 반환
 */

const GUIDE_COLOR = '#f43f5e';
const SNAP_THRESHOLD = 5;
const GUIDE_EXTEND = 10; // 오브젝트 엣지 너머 연장 픽셀

/**
 * 오브젝트의 6개 엣지 포인트 계산 (캔버스 좌표)
 * getCenterPoint() + getScaledWidth/Height()로 origin/scale 자동 처리
 * 회전 객체는 삼각함수로 축 정렬 바운딩 박스(AABB) 계산
 * @param {fabric.Object} obj
 * @returns {{ left: number, top: number, right: number, bottom: number, centerX: number, centerY: number }}
 */
export const getObjectEdges = (obj) => {
  const center = obj.getCenterPoint();
  const w = obj.getScaledWidth();
  const h = obj.getScaledHeight();
  const angle = obj.angle || 0;

  if (Math.abs(angle % 360) < 0.1) {
    return {
      left: center.x - w / 2,
      top: center.y - h / 2,
      right: center.x + w / 2,
      bottom: center.y + h / 2,
      centerX: center.x,
      centerY: center.y,
    };
  }

  // 회전 있음: AABB 계산
  const rad = (angle * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const bboxW = w * cos + h * sin;
  const bboxH = w * sin + h * cos;
  return {
    left: center.x - bboxW / 2,
    top: center.y - bboxH / 2,
    right: center.x + bboxW / 2,
    bottom: center.y + bboxH / 2,
    centerX: center.x,
    centerY: center.y,
  };
};

/**
 * 정렬 포인트 수집 (드래그 시작 시 1회 호출하고 캐시)
 * 각 포인트에 소스 오브젝트의 범위 정보 포함 (구간 제한 렌더링용)
 *
 * vPoint: { value, sourceTop, sourceBottom } - 수직 가이드 → 세로 구간 필요
 * hPoint: { value, sourceLeft, sourceRight } - 수평 가이드 → 가로 구간 필요
 */
export const collectAlignPoints = (objects, movingObj, canvasSize) => {
  const hPoints = [];
  const vPoints = [];

  // 캔버스 엣지/중앙: 캔버스 전체 구간
  vPoints.push(
    { value: 0, sourceTop: 0, sourceBottom: canvasSize.height },
    { value: canvasSize.width / 2, sourceTop: 0, sourceBottom: canvasSize.height },
    { value: canvasSize.width, sourceTop: 0, sourceBottom: canvasSize.height }
  );
  hPoints.push(
    { value: 0, sourceLeft: 0, sourceRight: canvasSize.width },
    { value: canvasSize.height / 2, sourceLeft: 0, sourceRight: canvasSize.width },
    { value: canvasSize.height, sourceLeft: 0, sourceRight: canvasSize.width }
  );

  // ActiveSelection 내부 오브젝트 ID 수집 (제외용)
  const excludeIds = new Set();
  if (movingObj.type === 'activeselection' || movingObj.type === 'activeSelection') {
    (movingObj.getObjects ? movingObj.getObjects() : []).forEach(child => {
      if (child.id) excludeIds.add(child.id);
    });
  }
  if (movingObj.id) excludeIds.add(movingObj.id);

  for (const obj of objects) {
    if (obj === movingObj) continue;
    if (obj.id && excludeIds.has(obj.id)) continue;

    const edges = getObjectEdges(obj);

    // 수직 정렬 포인트 (X값) + 소스의 세로 범위
    vPoints.push(
      { value: edges.left, sourceTop: edges.top, sourceBottom: edges.bottom },
      { value: edges.right, sourceTop: edges.top, sourceBottom: edges.bottom },
      { value: edges.centerX, sourceTop: edges.top, sourceBottom: edges.bottom }
    );

    // 수평 정렬 포인트 (Y값) + 소스의 가로 범위
    hPoints.push(
      { value: edges.top, sourceLeft: edges.left, sourceRight: edges.right },
      { value: edges.bottom, sourceLeft: edges.left, sourceRight: edges.right },
      { value: edges.centerY, sourceLeft: edges.left, sourceRight: edges.right }
    );
  }

  return { hPoints, vPoints };
};

/**
 * 스냅 계산
 * 가이드라인에 구간 정보(start/end) 포함
 * @param {fabric.Object} movingObj
 * @param {{ hPoints: Array, vPoints: Array }} alignPoints
 * @param {number} threshold
 * @returns {{ deltaX: number, deltaY: number, guides: Array<{ orientation: 'v'|'h', position: number, start: number, end: number }> }}
 */
export const calculateSnap = (movingObj, alignPoints, threshold) => {
  const edges = getObjectEdges(movingObj);
  const movingVPoints = [edges.left, edges.right, edges.centerX];
  const movingHPoints = [edges.top, edges.bottom, edges.centerY];

  let bestDx = null;
  let bestDxDist = Infinity;
  let bestVGuide = null;

  let bestDy = null;
  let bestDyDist = Infinity;
  let bestHGuide = null;

  // X축 (수직 가이드라인)
  for (const mp of movingVPoints) {
    for (const ap of alignPoints.vPoints) {
      const dist = Math.abs(mp - ap.value);
      if (dist < threshold && dist < bestDxDist) {
        bestDxDist = dist;
        bestDx = ap.value - mp;
        // 구간: 이동 오브젝트와 소스 오브젝트의 세로 범위 합집합 + 연장
        const start = Math.min(edges.top, ap.sourceTop) - GUIDE_EXTEND;
        const end = Math.max(edges.bottom, ap.sourceBottom) + GUIDE_EXTEND;
        bestVGuide = { orientation: 'v', position: ap.value, start, end };
      }
    }
  }

  // Y축 (수평 가이드라인)
  for (const mp of movingHPoints) {
    for (const ap of alignPoints.hPoints) {
      const dist = Math.abs(mp - ap.value);
      if (dist < threshold && dist < bestDyDist) {
        bestDyDist = dist;
        bestDy = ap.value - mp;
        // 구간: 이동 오브젝트와 소스 오브젝트의 가로 범위 합집합 + 연장
        const start = Math.min(edges.left, ap.sourceLeft) - GUIDE_EXTEND;
        const end = Math.max(edges.right, ap.sourceRight) + GUIDE_EXTEND;
        bestHGuide = { orientation: 'h', position: ap.value, start, end };
      }
    }
  }

  const guides = [];
  if (bestVGuide) guides.push(bestVGuide);
  if (bestHGuide) guides.push(bestHGuide);

  return {
    deltaX: bestDx || 0,
    deltaY: bestDy || 0,
    guides: deduplicateGuides(guides),
  };
};

/**
 * 동일 위치/방향 가이드라인 병합
 */
export const deduplicateGuides = (guides) => {
  const seen = new Set();
  return guides.filter(g => {
    const key = `${g.orientation}_${Math.round(g.position * 10)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * 하위 캔버스(contextContainer)에 가이드라인 렌더링
 * 구간 제한: 이동 오브젝트와 정렬 대상 사이만 표시
 * ctx.transform()으로 기존 retina 스케일링 위에 뷰포트 변환 적용
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<{ orientation: 'v'|'h', position: number, start: number, end: number }>} guides
 * @param {number[]} vpt - canvas.viewportTransform (6-element array)
 * @param {number} [thickness=1.5] - 가이드라인 두께 (CSS px 기준)
 */
export const renderGuideLines = (ctx, guides, vpt, thickness = 1.5) => {
  if (!guides.length) return;
  const zoom = vpt[0];

  ctx.save();
  ctx.transform(vpt[0], vpt[1], vpt[2], vpt[3], vpt[4], vpt[5]);
  ctx.strokeStyle = GUIDE_COLOR;
  ctx.lineWidth = thickness / zoom;
  ctx.setLineDash([4 / zoom, 4 / zoom]);

  for (const guide of guides) {
    ctx.beginPath();
    if (guide.orientation === 'v') {
      // 수직선: start~end는 Y 구간
      ctx.moveTo(guide.position, guide.start);
      ctx.lineTo(guide.position, guide.end);
    } else {
      // 수평선: start~end는 X 구간
      ctx.moveTo(guide.start, guide.position);
      ctx.lineTo(guide.end, guide.position);
    }
    ctx.stroke();
  }

  ctx.restore();
};

export { SNAP_THRESHOLD };
